import { type FunctionComponent, type HTMLAttributes, type ReactNode, useEffect, useRef } from 'react';

import styles from './StickyWidthValue.module.css';

const digitRegex = /\d/g;
const replaceDigits = (input: string): string => {
  return input.replace(digitRegex, '0');
};

export interface StickyWidthValueProps extends Omit<HTMLAttributes<HTMLDivElement>, 'prefix'> {
  /** Instead of determining placeholder from value of children, it can be passed as a prop if needed */
  placeholder?: string;
  /** React Node to be rendered before the value */
  prefix?: ReactNode;
}

export const StickyWidthValue: FunctionComponent<StickyWidthValueProps> = ({
  children,
  placeholder,
  prefix,
  ...rest
}) => {
  const placeholderRef = useRef<string>(placeholder ?? '');
  const containerElementRef = useRef<HTMLDivElement>(null);
  const valueElementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updatePlaceholder = (newPlaceholder: string): void => {
      if (!containerElementRef.current) return;
      const parsedPlaceholder = replaceDigits(newPlaceholder);
      if (newPlaceholder.length > placeholderRef.current.length) {
        containerElementRef.current.dataset['placeholder'] = parsedPlaceholder;
        placeholderRef.current = parsedPlaceholder;
      }
    };

    if (placeholder) {
      updatePlaceholder(placeholder);
      return;
    }

    let mutationObserver: MutationObserver | undefined;
    if (valueElementRef.current) {
      mutationObserver = new MutationObserver((records) => {
        records.forEach((record) => {
          if (record.type === 'characterData') {
            const data = (record.target as CharacterData).data;
            updatePlaceholder(data);
          }
        });
      });
      mutationObserver.observe(valueElementRef.current, {
        childList: true,
        subtree: true,
        characterData: true,
      });
      updatePlaceholder(valueElementRef.current.innerText);
    }

    return (): void => {
      mutationObserver?.disconnect();
    };
  }, [placeholder]);

  return (
    <div
      className={styles.stickyWidthValue}
      data-placeholder={placeholderRef.current}
      ref={containerElementRef}
      {...rest}
    >
      <div className={styles.stickyWidthValue__content}>
        {prefix ? <div className={styles.stickyWidthValue__prefix}>{prefix}</div> : null}
        <span className={styles.stickyWidthValue__value} ref={valueElementRef}>
          {children}
        </span>
      </div>
    </div>
  );
};
